// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, GripVertical } from 'lucide-react';
import { toast } from 'sonner';
import { formatTime } from '@/lib/dates';

interface ScheduleGridViewProps {
  tournamentId: string;
  courts: any[];
  eventDates: string[];
  onMatchClick?: (match: any) => void;
}

const ScheduleGridView: React.FC<ScheduleGridViewProps> = ({
  tournamentId,
  courts,
  eventDates,
  onMatchClick
}) => {
  const [matches, setMatches] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(eventDates[0] || new Date().toISOString().split('T')[0]);
  const [draggedMatch, setDraggedMatch] = useState<any>(null);

  // Generate time slots for the day (8am to 8pm, every 30 min)
  const timeSlots: string[] = [];
  for (let hour = 8; hour <= 20; hour++) {
    for (let min = 0; min < 60; min += 30) {
      const time = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
      timeSlots.push(time);
    }
  }

  const fetchMatches = async () => {
    const { data, error } = await supabase
      .from('tournament_matches')
      .select(`
        *,
        team1:team1_id(id, name),
        team2:team2_id(id, name)
      `)
      .eq('tournament_id', tournamentId);

    if (!error) {
      setMatches(data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchMatches();
  }, [tournamentId]);

  useEffect(() => {
    if (eventDates.length > 0 && !eventDates.includes(selectedDate)) {
      setSelectedDate(eventDates[0]);
    }
  }, [eventDates]);

  // Get match at specific court and time
  const getMatchAtSlot = (courtName: string, time: string) => {
    return matches.find(match => {
      if (!match.scheduled_time || match.court_field !== courtName) return false;
      const matchDate = match.scheduled_time.split('T')[0];
      const matchTime = match.scheduled_time.split('T')[1]?.substring(0, 5);
      return matchDate === selectedDate && matchTime === time;
    });
  };

  // Get unscheduled matches
  const unscheduledMatches = matches.filter(m => !m.scheduled_time || !m.court_field);

  // Handle drag start
  const handleDragStart = (e: React.DragEvent, match: any) => {
    setDraggedMatch(match);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', match.id);
  };

  // Handle drag over
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  // Handle drop
  const handleDrop = async (e: React.DragEvent, courtName: string, time: string) => {
    e.preventDefault();
    
    if (!draggedMatch) return;

    // Check if slot is already occupied
    const existingMatch = getMatchAtSlot(courtName, time);
    if (existingMatch && existingMatch.id !== draggedMatch.id) {
      toast.error('This slot is already occupied');
      setDraggedMatch(null);
      return;
    }

    // Update match with new time and court
    const scheduledTime = `${selectedDate}T${time}:00`;
    
    const { error } = await supabase
      .from('tournament_matches')
      .update({
        scheduled_time: scheduledTime,
        court_field: courtName
      })
      .eq('id', draggedMatch.id);

    if (error) {
      toast.error('Failed to update match');
    } else {
      toast.success('Match scheduled');
      fetchMatches();
    }

    setDraggedMatch(null);
  };

  // Handle removing match from schedule
  const handleUnschedule = async (e: React.DragEvent) => {
    e.preventDefault();
    
    if (!draggedMatch) return;

    const { error } = await supabase
      .from('tournament_matches')
      .update({
        scheduled_time: null,
        court_field: null
      })
      .eq('id', draggedMatch.id);

    if (error) {
      toast.error('Failed to unschedule match');
    } else {
      toast.success('Match unscheduled');
      fetchMatches();
    }

    setDraggedMatch(null);
  };

  if (loading) {
    return <div className="text-center py-8 text-muted-foreground">Loading schedule...</div>;
  }

  if (courts.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No courts configured. Add courts first to use the grid view.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Date Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const idx = eventDates.indexOf(selectedDate);
            if (idx > 0) setSelectedDate(eventDates[idx - 1]);
          }}
          disabled={eventDates.indexOf(selectedDate) <= 0}
        >
          <ChevronLeft className="w-4 h-4" />
        </Button>
        <span className="font-medium">
          {new Date(selectedDate + 'T12:00:00').toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric'
          })}
        </span>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const idx = eventDates.indexOf(selectedDate);
            if (idx < eventDates.length - 1) setSelectedDate(eventDates[idx + 1]);
          }}
          disabled={eventDates.indexOf(selectedDate) >= eventDates.length - 1}
        >
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Unscheduled Matches */}
      {unscheduledMatches.length > 0 && (
        <Card>
          <CardHeader className="py-3">
            <CardTitle className="text-sm">Unscheduled Matches ({unscheduledMatches.length})</CardTitle>
          </CardHeader>
          <CardContent
            className="flex flex-wrap gap-2 min-h-[60px]"
            onDragOver={handleDragOver}
            onDrop={handleUnschedule}
          >
            {unscheduledMatches.map(match => (
              <div
                key={match.id}
                draggable
                onDragStart={(e) => handleDragStart(e, match)}
                className="flex items-center gap-1 px-2 py-1 bg-secondary border border-border rounded text-sm cursor-move hover:bg-secondary/80 transition-colors"
              >
                <GripVertical className="w-3 h-3 text-muted-foreground" />
                <span>{match.team1?.name || 'TBD'} vs {match.team2?.name || 'TBD'}</span>
                <Badge variant="outline" className="ml-1 text-xs">R{match.round_number}</Badge>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Grid View */}
      <div className="overflow-x-auto border rounded-lg">
        <div className="min-w-[800px]">
          {/* Header - Time slots */}
          <div className="flex border-b">
            <div className="w-24 flex-shrink-0 p-2 font-medium bg-muted">Court</div>
            {timeSlots.map(time => (
              <div key={time} className="w-28 flex-shrink-0 p-2 text-center text-xs border-l bg-muted">
                {formatTime(time)}
              </div>
            ))}
          </div>

          {/* Rows - Courts */}
          {courts.map(court => (
            <div key={court.id || court.name} className="flex border-b last:border-b-0">
              <div className="w-24 flex-shrink-0 p-2 font-medium bg-muted border-r text-sm">
                {court.name}
              </div>
              {timeSlots.map(time => {
                const match = getMatchAtSlot(court.name, time);
                return (
                  <div
                    key={`${court.name}-${time}`}
                    className={`w-28 flex-shrink-0 p-1 border-l min-h-[60px] transition-colors ${
                      draggedMatch ? 'bg-primary/5 hover:bg-primary/10' : 'hover:bg-muted/50'
                    }`}
                    onDragOver={handleDragOver}
                    onDrop={(e) => handleDrop(e, court.name, time)}
                  >
                    {match && (
                      <div
                        draggable
                        onDragStart={(e) => handleDragStart(e, match)}
                        onClick={() => onMatchClick?.(match)}
                        className="p-1 bg-primary/10 border border-primary/30 rounded text-xs cursor-move hover:bg-primary/20 transition-colors"
                      >
                        <div className="font-medium truncate">
                          {match.team1?.name || 'TBD'}
                        </div>
                        <div className="truncate text-muted-foreground">
                          vs {match.team2?.name || 'TBD'}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
        <span>💡 Drag matches to schedule them on courts</span>
        <span>📋 Drag to "Unscheduled" to remove from schedule</span>
      </div>
    </div>
  );
};

export default ScheduleGridView;
